/**
 * Authentication Redux Slice
 * 
 * SOLID Principles Applied:
 * - Single Responsibility: Only manages authentication state (reducers)
 * - Separation of Concerns: 
 *   • State management (this file)
 *   • Async operations (authThunks.ts)
 *   • Data access (authSelectors.ts)
 * - Open/Closed: Easy to add new reducers without modifying existing ones
 * - Dependency Inversion: Depends on thunks abstraction, not implementation
 * 
 * This file contains:
 * - Initial state definition
 * - Synchronous reducers
 * - Async thunk handlers (extraReducers)
 * - Slice export
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authService } from '../../../services/api/auth.service';
import {
  IAuthState,
  IUser,
  IAuthResponse,
} from '../../../types/auth.types';

// Import async thunks (Separation of Concerns)
import {
  loginUser,
  registerUser,
  logoutUser,
  refreshToken,
  getCurrentUser,
  updateUserProfile,
  changePassword,
  unlinkGoogleAccount,
  linkGoogleAccount,
  verifyTwoFactorLogin,
  authenticateWithGoogle,
  authenticateWithGoogleOneTap,
  OAUTH_CANCELLED,
} from './authThunks';

// ============================================
// INITIAL STATE
// ============================================

const initialState: IAuthState = {
  user: null,
  accessToken: null,
  // Refresh token is stored in httpOnly cookie (not in state for security)
  isAuthenticated: false,
  isLoading: false,
  isLoggingIn: false,
  isRegistering: false,
  isLoggingOut: false,
  error: null,
  lastActivity: null,
  rememberMe: false,
  bootstrapComplete: false,
  sessionMessage: null,
  twoFactorPending: false,
  twoFactorToken: null,
  twoFactorEmail: null,
};

// ============================================
// SLICE (State Management)
// ============================================

const authSlice = createSlice({
  name: 'auth',
  initialState,
  
  // ============================================
  // SYNCHRONOUS REDUCERS
  // ============================================
  reducers: {
    /**
     * Clear error state
     * 
     * Usage:
     *   dispatch(clearError())
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Update last activity timestamp
     * 
     * Used for session timeout tracking
     * 
     * Usage:
     *   dispatch(updateLastActivity())
     */
    updateLastActivity: (state) => {
      state.lastActivity = new Date().toISOString();
    },

    /**
     * Set user data manually
     * 
     * Used for session restoration or manual updates
     * 
     * Usage:
     *   dispatch(setUser(userData))
     */
    setUser: (state, action: PayloadAction<IUser>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },

    /**
     * Set access token manually
     * 
     * Used for session restoration
     * Note: Refresh token is in httpOnly cookie, not in state
     * 
     * Usage:
     *   dispatch(setAccessToken('token'))
     */
    setAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
    },

    /**
     * Restore session from localStorage
     * 
     * Called on app initialization
     * Note: User data should be fetched separately with getCurrentUser thunk
     * Refresh token is in httpOnly cookie (not in localStorage)
     * 
     * Usage:
     *   dispatch(restoreSession())
     */
    restoreSession: (state) => {
      const accessToken = authService.getStoredAccessToken();
      if (accessToken) {
        state.accessToken = accessToken;
      }
    },

    /**
     * Reset auth state to initial state
     * 
     * Used after logout or session expiration
     * 
     * Usage:
     *   dispatch(resetAuthState())
     */
    resetAuthState: (state) => ({
      ...initialState,
      bootstrapComplete: state.bootstrapComplete,
    }),

    /** Marks initial session restore as finished (useAuthInit). */
    completeAuthBootstrap: (state) => {
      state.bootstrapComplete = true;
    },

    /** Clears session banner message (e.g. after login form is shown). */
    clearSessionMessage: (state) => {
      state.sessionMessage = null;
    },

    /**
     * Session invalidated (401, expired token, inconsistent auth).
     * Keeps bootstrapComplete so route guards do not re-enter bootstrap loading.
     */
    clearTwoFactorChallenge: (state) => {
      state.twoFactorPending = false;
      state.twoFactorToken = null;
      state.twoFactorEmail = null;
    },

    setTwoFactorChallenge: (
      state,
      action: PayloadAction<{ token: string; email?: string | null }>,
    ) => {
      state.twoFactorPending = true;
      state.twoFactorToken = action.payload.token;
      state.twoFactorEmail = action.payload.email ?? null;
      state.error = null;
    },

    sessionExpired: (state, action: PayloadAction<string | undefined>) => {
      const message =
        action.payload ?? 'Your session has expired. Please sign in again.';
      return {
        ...initialState,
        bootstrapComplete: true,
        sessionMessage: message,
      };
    },
  },

  // ============================================
  // ASYNC THUNK HANDLERS (extraReducers)
  // ============================================
  extraReducers: (builder) => {
    // ========================================
    // LOGIN USER
    // ========================================
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoggingIn = true;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<IAuthResponse>) => {
        state.isLoggingIn = false;
        state.isLoading = false;
        state.error = null;
        state.sessionMessage = null;

        if (action.payload.requiresTwoFactor && action.payload.twoFactorToken) {
          state.twoFactorPending = true;
          state.twoFactorToken = action.payload.twoFactorToken;
          state.twoFactorEmail = action.payload.user?.email ?? null;
          state.isAuthenticated = false;
          return;
        }

        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.twoFactorPending = false;
        state.twoFactorToken = null;
        state.twoFactorEmail = null;
        state.lastActivity = new Date().toISOString();
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoggingIn = false;
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(verifyTwoFactorLogin.pending, (state) => {
        state.isLoggingIn = true;
        state.error = null;
      })
      .addCase(verifyTwoFactorLogin.fulfilled, (state, action: PayloadAction<IAuthResponse>) => {
        state.isLoggingIn = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.twoFactorPending = false;
        state.twoFactorToken = null;
        state.twoFactorEmail = null;
        state.lastActivity = new Date().toISOString();
        state.error = null;
      })
      .addCase(verifyTwoFactorLogin.rejected, (state, action) => {
        state.isLoggingIn = false;
        state.error = action.payload as string;
      });

    // ========================================
    // GOOGLE OAUTH
    // ========================================
    builder
      .addCase(authenticateWithGoogle.pending, (state) => {
        state.isLoggingIn = true;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(authenticateWithGoogle.fulfilled, (state, action: PayloadAction<IAuthResponse>) => {
        state.isLoggingIn = false;
        state.isLoading = false;
        state.error = null;
        state.sessionMessage = null;

        if (action.payload.requiresTwoFactor && action.payload.twoFactorToken) {
          state.twoFactorPending = true;
          state.twoFactorToken = action.payload.twoFactorToken;
          state.twoFactorEmail = action.payload.user?.email ?? null;
          state.isAuthenticated = false;
          return;
        }

        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.twoFactorPending = false;
        state.twoFactorToken = null;
        state.twoFactorEmail = null;
        state.lastActivity = new Date().toISOString();
      })
      .addCase(authenticateWithGoogle.rejected, (state, action) => {
        state.isLoggingIn = false;
        state.isLoading = false;
        if (action.payload === OAUTH_CANCELLED) {
          state.error = null;
          return;
        }
        state.error = action.payload as string;
      });

    // ========================================
    // GOOGLE ONE TAP
    // ========================================
    builder
      .addCase(authenticateWithGoogleOneTap.pending, (state) => {
        state.isLoggingIn = true;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(authenticateWithGoogleOneTap.fulfilled, (state, action: PayloadAction<IAuthResponse>) => {
        state.isLoggingIn = false;
        state.isLoading = false;
        state.error = null;
        state.sessionMessage = null;

        if (action.payload.requiresTwoFactor && action.payload.twoFactorToken) {
          state.twoFactorPending = true;
          state.twoFactorToken = action.payload.twoFactorToken;
          state.twoFactorEmail = action.payload.user?.email ?? null;
          state.isAuthenticated = false;
          return;
        }

        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.twoFactorPending = false;
        state.twoFactorToken = null;
        state.twoFactorEmail = null;
        state.lastActivity = new Date().toISOString();
      })
      .addCase(authenticateWithGoogleOneTap.rejected, (state, action) => {
        state.isLoggingIn = false;
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // ========================================
    // REGISTER USER
    // ========================================
    builder
      .addCase(registerUser.pending, (state) => {
        state.isRegistering = true;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action: PayloadAction<IAuthResponse>) => {
        state.isRegistering = false;
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        // Refresh token is in httpOnly cookie (not in state)
        state.lastActivity = new Date().toISOString();
        state.error = null;
        state.sessionMessage = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isRegistering = false;
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      });

    // ========================================
    // LOGOUT USER
    // ========================================
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoggingOut = true;
      })
      .addCase(logoutUser.fulfilled, (state) => ({
        ...initialState,
        bootstrapComplete: state.bootstrapComplete,
      }))
      .addCase(logoutUser.rejected, (state) => {
        state.isLoggingOut = false;
        return {
          ...initialState,
          bootstrapComplete: state.bootstrapComplete,
        };
      });

    // ========================================
    // REFRESH TOKEN
    // ========================================
    builder
      .addCase(refreshToken.pending, (state) => {
        if (state.bootstrapComplete) {
          state.isLoading = true;
        }
      })
      .addCase(refreshToken.fulfilled, (state, action: PayloadAction<IAuthResponse>) => {
        state.isLoading = false;
        state.accessToken = action.payload.access_token;
        // Refresh token is in httpOnly cookie (automatically updated by backend)
        state.lastActivity = new Date().toISOString();
      })
      .addCase(refreshToken.rejected, (state) => {
        state.isLoading = false;
        return {
          ...initialState,
          bootstrapComplete: true,
        };
      });

    // ========================================
    // GET CURRENT USER
    // ========================================
    builder
      .addCase(getCurrentUser.pending, (state) => {
        if (state.bootstrapComplete) {
          state.isLoading = true;
        }
      })
      .addCase(getCurrentUser.fulfilled, (state, action: PayloadAction<IUser>) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.lastActivity = new Date().toISOString();
        state.sessionMessage = null;
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.isLoading = false;
        return {
          ...initialState,
          bootstrapComplete: true,
          sessionMessage: 'Your session has expired. Please sign in again.',
        };
      });

    // ========================================
    // UPDATE USER PROFILE
    // ========================================
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action: PayloadAction<IUser>) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // ========================================
    // CHANGE PASSWORD
    // ========================================
    builder
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // ========================================
    // DISCONNECT GOOGLE
    // ========================================
    builder
      .addCase(unlinkGoogleAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(unlinkGoogleAccount.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(unlinkGoogleAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // ========================================
    // CONNECT GOOGLE
    // ========================================
    builder
      .addCase(linkGoogleAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(linkGoogleAccount.fulfilled, (state, action: PayloadAction<IUser>) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(linkGoogleAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// ============================================
// EXPORTS
// ============================================

// Export synchronous actions
export const {
  clearError,
  updateLastActivity,
  setUser,
  setAccessToken,
  restoreSession,
  resetAuthState,
  completeAuthBootstrap,
  clearSessionMessage,
  clearTwoFactorChallenge,
  setTwoFactorChallenge,
  sessionExpired,
} = authSlice.actions;

// Export reducer as default
export default authSlice.reducer;
